import React, { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { addDays, format, parseISO } from 'date-fns';
import { Download, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { TNaerasDashboardPeziWater, TNaerasPanel, TNaerasPeziWaterDay } from '@/data/types';
import { useAppDispatch } from '@/data/store/utils/redux';
import { getNaerasPeziWaterAsync } from '@/data/store/actions';
import { NaerasErrorCodes, parseNaerasError } from '@/pages/Calculate/utils/naerasBid';
import useNotify from '@/hooks/useNotify';
import PanelCard, { Kpi } from './PanelCard';
import { COLORS, MISSING, isNum, m3, mwh, num, shortDate } from '../utils/format';

interface PeziWaterPanelProps {
  panel: TNaerasPanel<TNaerasDashboardPeziWater> | undefined;
  /** Today on the Greek calendar — the export range ends the day before. */
  today: string | undefined;
}

const DAY_CHOICES = [3, 4, 5, 6, 7] as const;
const MAX_RANGE_DAYS = 366;

interface Point {
  date: string;
  label: string;
  value: number | null;
  mwh: number | null;
  estimate: boolean;
}

const DayTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;

  const point: Point = payload[0].payload;

  return (
    <div className="rounded-2xl border border-slate-100 bg-white/95 p-3 shadow-xl backdrop-blur-md">
      <p className="mb-1 text-xs font-bold text-slate-800">
        {point.label}
        {point.estimate ? ' · εκτίμηση' : ''}
      </p>
      <p className="text-xs font-extrabold text-slate-900">{m3(point.value)}</p>
      <p className="text-[11px] font-medium text-slate-500">{mwh(point.mwh)} Προεσπέρας</p>
    </div>
  );
};

/** Sum of the values, or null when every one is missing. */
function total(days: TNaerasPeziWaterDay[], key: 'pezi_water_m3' | 'e_u_mwh'): number | null {
  const values = days.map((day) => day[key]).filter(isNum);

  return values.length ? values.reduce((sum, value) => sum + value, 0) : null;
}

/** Rounded once here, so the sheet never shows float noise like 4.9042E+15. */
const cell = (value: number | null | undefined, decimals: number) =>
  isNum(value) ? Number(value.toFixed(decimals)) : null;

/** Number format per column; null = text. */
const COLUMNS: { header: string; format: string | null; width: number }[] = [
  { header: 'Ημερομηνία', format: null, width: 12 },
  { header: 'Υδατοπτώσεις από Πέζι (m³)', format: '#,##0', width: 26 },
  { header: 'Παραγωγή Προεσπέρας (MWh)', format: '#,##0.000', width: 26 },
  { header: 'Παραγωγή Κάτω Προεσπέρας (MWh)', format: '#,##0.000', width: 30 },
  { header: 'Λόγος παραγωγής', format: '0.000', width: 16 },
  { header: 'Περίοδος προσφοράς', format: null, width: 18 },
];

/**
 * An .xlsx rather than a CSV: a CSV's decimal mark is read by the opener's
 * regional settings, so on a dot-decimal PC 3473,788… came out as
 * 3,473,788,364,635,820. Numeric cells with a number format read the same
 * everywhere.
 */
function downloadXlsx(days: TNaerasPeziWaterDay[], fileName: string) {
  const rows = days.map((day) => [
    format(parseISO(day.date), 'dd/MM/yyyy'),
    cell(day.pezi_water_m3, 0),
    cell(day.e_u_mwh, 3),
    cell(day.e_l_mwh, 3),
    cell(day.production_ratio, 3),
    day.is_winter == null ? null : day.is_winter ? 'Υγρή' : 'Ξηρή',
  ]);
  const sheet = XLSX.utils.aoa_to_sheet([COLUMNS.map((column) => column.header), ...rows]);

  rows.forEach((_, r) =>
    COLUMNS.forEach((column, c) => {
      const target = sheet[XLSX.utils.encode_cell({ r: r + 1, c })];
      if (column.format && target?.t === 'n') target.z = column.format;
    })
  );
  sheet['!cols'] = COLUMNS.map((column) => ({ wch: column.width }));

  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Υδατοπτώσεις Πεζίου');
  XLSX.writeFile(book, fileName);
}

/**
 * §2.7 — «Υδατοπτώσεις από Δεξαμενή Πεζίου»: m³ the Προεσπέρα took from Πέζι
 * per day. The last N measured days are solid, today's estimate outlined. A
 * day that was never reconciled stays on the axis as a gap and leaves the
 * total incomplete rather than quietly smaller.
 */
export const PeziWaterPanel: React.FC<PeziWaterPanelProps> = ({ panel, today }) => {
  const dispatch = useAppDispatch();
  const { showToast } = useNotify();

  const [dayCount, setDayCount] = useState<number>(7);
  const yesterday = today ? format(addDays(parseISO(today), -1), 'yyyy-MM-dd') : undefined;
  const [rangeFrom, setRangeFrom] = useState<string>('');
  const [rangeTo, setRangeTo] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);

  // Until the user picks a range, the export covers the days on the chart
  const exportTo = rangeTo || yesterday || '';
  const exportFrom =
    rangeFrom || (exportTo ? format(addDays(parseISO(exportTo), -(dayCount - 1)), 'yyyy-MM-dd') : '');

  const rangeDays =
    exportFrom && exportTo
      ? Math.round((parseISO(exportTo).getTime() - parseISO(exportFrom).getTime()) / 86_400_000) + 1
      : 0;
  const rangeError = !exportFrom || !exportTo
    ? 'Επιλέξτε διάστημα'
    : rangeDays < 1
    ? 'Η αρχή είναι μετά το τέλος'
    : rangeDays > MAX_RANGE_DAYS
    ? `Έως ${MAX_RANGE_DAYS} ημέρες`
    : null;

  const handleExport = async () => {
    if (rangeError || isExporting) return;

    setIsExporting(true);

    try {
      const days = await dispatch(
        getNaerasPeziWaterAsync({ params: { from: exportFrom, to: exportTo }, silent: true })
      ).unwrap();

      downloadXlsx(days, `pezi-water-${exportFrom}_${exportTo}.xlsx`);
    } catch (error) {
      const { code, message } = parseNaerasError(error);

      showToast({
        type: 'error',
        title: 'Η εξαγωγή Excel απέτυχε',
        message:
          code === NaerasErrorCodes.INVALID_RANGE
            ? `Μη έγκυρο διάστημα: έως χθες και το πολύ ${MAX_RANGE_DAYS} ημέρες.`
            : message || 'Τα δεδομένα δεν είναι διαθέσιμα.',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <PanelCard
      title="Υδατοπτώσεις από Δεξαμενή Πεζίου"
      subtitle="Νερό που πήρε η ΜΥΗΣ Προεσπέρας από το Πέζι, ανά ημέρα (m³)"
      panel={panel}
      delay={0.6}
      aside={() => (
        <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 p-1">
          {DAY_CHOICES.map((count) => (
            <button
              key={count}
              onClick={() => setDayCount(count)}
              className={`rounded-lg px-2.5 py-1 text-xs font-bold transition ${
                dayCount === count ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              {count}ημ
            </button>
          ))}
        </div>
      )}
    >
      {(data) => {
        const shown = data.days.slice(-dayCount);
        const latest = data.days[data.days.length - 1] ?? null;
        const hasGap = shown.some((day) => !isNum(day.pezi_water_m3));
        const totalM3 = total(shown, 'pezi_water_m3');
        const totalMwh = total(shown, 'e_u_mwh');
        const totalLabel = shown.length
          ? `Σύνολο ${shortDate(shown[0].date)}–${shortDate(shown[shown.length - 1].date)}`
          : 'Σύνολο';

        const points: Point[] = [
          ...shown.map((day) => ({
            date: day.date,
            label: shortDate(day.date),
            value: day.pezi_water_m3,
            mwh: day.e_u_mwh,
            estimate: false,
          })),
          ...(data.today
            ? [
                {
                  date: data.today.date,
                  label: data.today.date ? `${shortDate(data.today.date)} εκτ.` : 'Σήμερα εκτ.',
                  value: data.today.pezi_water_m3,
                  mwh: data.today.e_u_mwh,
                  estimate: true,
                },
              ]
            : []),
        ];

        return (
          <>
            <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Kpi
                label="Χθες"
                value={m3(latest?.pezi_water_m3)}
                sub={`${mwh(latest?.e_u_mwh)} Προεσπέρας`}
                tone="hydro"
              />
              <Kpi
                label="Σήμερα · εκτ."
                value={m3(data.today?.pezi_water_m3)}
                sub={`${mwh(data.today?.e_u_mwh)} Προεσπέρας`}
                tone="hydro"
              />
              <Kpi
                label={hasGap ? `${totalLabel} (ελλιπές)` : totalLabel}
                value={m3(totalM3)}
                sub={
                  hasGap
                    ? `${mwh(totalMwh)} · λείπουν ${shown.filter((day) => !isNum(day.pezi_water_m3)).length} ημέρες`
                    : `${mwh(totalMwh)} Προεσπέρας`
                }
                tone="hydro"
              />
            </div>

            {points.length === 0 ? (
              <div className="flex h-64 items-center justify-center rounded-2xl bg-slate-50 text-2xl font-bold text-slate-300">
                {MISSING}
              </div>
            ) : (
              <div className="h-64 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={points} margin={{ top: 16, right: 10, left: 0, bottom: 0 }} barSize={44}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#64748b', fontSize: 11, fontWeight: 700 }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }}
                      tickFormatter={(v) => num(v, 0)}
                      width={64}
                    />
                    <Tooltip content={<DayTooltip />} cursor={{ fill: 'rgba(241, 245, 249, 0.6)' }} />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                      {points.map((point) => (
                        <Cell
                          key={point.date ?? point.label}
                          fill={point.estimate ? '#ffffff' : COLORS.hydro}
                          stroke={COLORS.hydro}
                          strokeWidth={point.estimate ? 2 : 0}
                          strokeDasharray={point.estimate ? '4 3' : undefined}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="mt-3 flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 pt-3">
              <div className="flex flex-wrap items-center gap-4 text-[11px] font-semibold text-slate-500">
                <span className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: COLORS.hydro }} />
                  Μέτρηση
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-sm border-2 border-dashed" style={{ borderColor: COLORS.hydro }} />
                  Εκτίμηση σήμερα
                </span>
              </div>

              {/* Any range up to a year, measured days only */}
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <input
                  type="date"
                  value={exportFrom}
                  max={exportTo || yesterday}
                  onChange={(event) => setRangeFrom(event.target.value)}
                  className="rounded-lg border border-slate-200 px-2 py-1 text-slate-700"
                />
                <span className="text-slate-400">–</span>
                <input
                  type="date"
                  value={exportTo}
                  max={yesterday}
                  onChange={(event) => setRangeTo(event.target.value)}
                  className="rounded-lg border border-slate-200 px-2 py-1 text-slate-700"
                />
                <button
                  onClick={handleExport}
                  disabled={Boolean(rangeError) || isExporting}
                  title={rangeError ?? 'Λήψη Excel για το διάστημα'}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1 font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
                >
                  {isExporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                  Excel
                </button>
              </div>
            </div>
          </>
        );
      }}
    </PanelCard>
  );
};

export default PeziWaterPanel;
